/**
 * The share sheet: a captured plate and a garden-carrying link.
 *
 * The privacy line this rests on is in `src/game/share.ts` and `AGENTS.md`:
 * the name is opt-in and off by default, and nothing is sent anywhere by the
 * app itself — the image and the link are handed to the player.
 */

import * as THREE from 'three';

import { BRAND } from '../content/brand';
import { TIERS } from '../content/tiers';
import type { SpeciesId } from '../content/species';
import type { ARBackend } from '../core/ARSession';
import type { AudioEngine } from '../core/AudioEngine';
import type { Garden } from '../game/Garden';
import { formatStampDate, type VisitorStamp } from '../game/progress';
import { encodeGarden, shareUrl } from '../game/share';
import { drawCard, drawCover } from '../ui/card';
import type { UI } from '../ui/UI';

export interface ShareHost {
  readonly ui: UI;
  readonly audio: AudioEngine;
  readonly renderer: THREE.WebGLRenderer;
  readonly scene: THREE.Scene;
  readonly camera: THREE.PerspectiveCamera;
  readonly garden: Garden;
  backend(): ARBackend | null;
  /** True once the plot is placed and the HUD is up. */
  playing(): boolean;
  visiting(): boolean;
  activeStamp(): VisitorStamp | null;
}

export class ShareSheet {
  private host: ShareHost;
  private lastCardUrl: string | null = null;

  constructor(host: ShareHost) {
    this.host = host;
  }

  /**
   * Render the garden and read the pixels back, with whatever is behind it.
   *
   * The render and the read have to happen in one task: without
   * `preserveDrawingBuffer` the buffer is cleared the moment control returns
   * to the browser. This is the same constraint the dev `__shot` route works
   * around, and the reason this is not an async function.
   *
   * Three cases, and the middle one is the whole point of this method:
   *
   * - **`preview`** has nothing behind the garden, so the render is done at a
   *   fixed 1200x900 and the plate comes out the same shape on every screen.
   * - **`camera`** has a live feed behind it. The canvas is cleared with alpha
   *   0 so the feed shows through, which means the raw capture is a
   *   transparent PNG of the garden alone. The feed is a `<video>` this page
   *   owns, so it is composited in here — but only at the *screen's* aspect,
   *   because re-rendering at 4:3 would change the camera's field of view and
   *   the garden would no longer sit where the player saw it against the room.
   * - **`webxr`** also has a backdrop and cannot hand it over: the runtime
   *   composites passthrough and the page never sees it. The card paints its
   *   own sky and floor for that case.
   */
  private captureGarden(): HTMLCanvasElement | null {
    const { renderer: r, scene, camera } = this.host;
    const presenting = r.xr.isPresenting;
    const backdrop = this.host.backend()?.backdropSource?.() ?? null;

    const size = r.getSize(new THREE.Vector2());
    // Read the ratio actually in use rather than recomputing it from
    // `devicePixelRatio` afterwards. Those are usually the same number and
    // were not here: restoring the assumption dropped the drawing buffer from
    // 1800x3200 to 900x1600 and every later frame rendered soft.
    const pixelRatio = r.getPixelRatio();
    const aspect = camera.aspect;

    // Resizing is only safe when nothing behind the garden has to stay lined
    // up with it.
    const reframe = !presenting && !backdrop;
    const w = reframe ? 1200 : Math.max(1, Math.round(size.x));
    const h = reframe ? 900 : Math.max(1, Math.round(size.y));

    try {
      if (reframe) {
        // `false` leaves the CSS size alone, so nothing on screen reflows.
        r.setPixelRatio(1);
        r.setSize(w, h, false);
        camera.aspect = w / h;
        camera.updateProjectionMatrix();
      }
      r.render(scene, camera);

      const out = document.createElement('canvas');
      out.width = w;
      out.height = h;
      const ctx = out.getContext('2d');
      if (!ctx) return null;
      // `.ar-video` is laid out `object-fit: cover` over the stage, so
      // cover-fitting it into the same rect reproduces what is on screen.
      if (backdrop) drawCover(ctx, backdrop, 0, 0, w, h);
      ctx.drawImage(r.domElement, 0, 0, w, h);
      return out;
    } catch {
      return null;
    } finally {
      if (reframe) {
        r.setPixelRatio(pixelRatio);
        r.setSize(size.x, size.y, false);
        camera.aspect = aspect;
        camera.updateProjectionMatrix();
      }
    }
  }

  /** The garden as a link seed. Also what a world-map publish carries. */
  seed(includeName: boolean): string {
    const now = Date.now();
    const { garden } = this.host;
    return encodeGarden({
      name: includeName ? (this.host.activeStamp()?.name ?? '') : '',
      tier: garden.tier,
      plants: garden.all.map((p) => ({
        speciesId: p.species.id as SpeciesId,
        slot: p.slot,
        ageMinutes: Math.round((now - p.sownAt) / 60000),
      })),
    });
  }

  show(): void {
    const { ui } = this.host;
    if (!this.host.playing()) return;
    if (this.host.visiting()) {
      // The plate says "N trees grown" over your name. Not for a garden you
      // are only visiting.
      ui.toast('Not yours to share', 'Go back to your own garden first.');
      return;
    }
    this.host.audio.tick();
    ui.showShare(this.host.activeStamp()?.name ?? null);
    this.refresh();
  }

  /** Rebuild the card and the link. Cheap enough to redo on every toggle. */
  refresh(): void {
    const { ui, garden } = this.host;
    // `captureGarden` hands back a finished canvas, which `drawCard` can take
    // directly — no data-URL round trip through an Image, and no await, which
    // matters because the capture must not be split across tasks.
    const shot = this.captureGarden();
    if (!shot) {
      ui.setShareNote('This browser would not hand back the picture.');
      return;
    }

    const trees = garden.treeCount;
    const tier = TIERS[garden.tier];
    const card = drawCard(shot, {
      name: ui.shareIncludesName ? (this.host.activeStamp()?.name ?? '') : '',
      headline: `${trees} ${trees === 1 ? 'tree' : 'trees'} grown`,
      meta: `${tier.name} · ${garden.capacity} beds · ${formatStampDate(Date.now())}`,
    });

    this.lastCardUrl = card.toDataURL('image/png');
    ui.setSharePreview(this.lastCardUrl);
  }

  async saveCard(): Promise<void> {
    if (!this.lastCardUrl) return;
    const name = BRAND.share.fileName;

    /*
     * Try the share sheet first. On a phone this is the difference between
     * "the picture is now in your camera roll somewhere" and "the picture is
     * in the message you were already writing", and this is a phone-first
     * app. A download anchor is the desktop path and the fallback.
     */
    const blob = await (await fetch(this.lastCardUrl)).blob();
    const file = new File([blob], name, { type: 'image/png' });
    const nav = navigator as Navigator & { canShare?(d: ShareData): boolean };
    if (nav.canShare?.({ files: [file] })) {
      try {
        await navigator.share({ files: [file], title: BRAND.share.title });
        return;
      } catch {
        // Cancelled, or refused. Fall through to the download.
      }
    }

    const a = document.createElement('a');
    a.href = this.lastCardUrl;
    a.download = name;
    a.click();
    this.host.ui.toast('Saved', 'The plate is in your downloads.');
  }

  async copyLink(): Promise<void> {
    const { ui } = this.host;
    const url = shareUrl(this.seed(ui.shareIncludesName));
    try {
      await navigator.clipboard.writeText(url);
      ui.toast('Link copied', 'Whoever opens it gets your garden to grow.');
    } catch {
      // Clipboard access is refused in plenty of contexts, and silently
      // failing to copy is worse than showing the link to select by hand.
      ui.setShareNote(url);
    }
  }
}
